import { useState, useEffect, useCallback, useRef } from 'react';
import { challengesApi, Challenge, Participant } from '@/api/challenges';
import { useAuth } from '@/contexts/AuthContext';
import { refineErrorMessage } from '@/utils/errorMessages';

export interface LeaderboardParticipant {
  rank: number;
  walletAddress: string;
  name: string;
  avatar: string;
  daysCompleted: number;
  totalRunDistance: number;
  totalRunDuration: number; // For marathon: finish time in seconds
  currentStreak: number;
  isWinner: boolean;
  isCurrentUser: boolean;
}

export interface LeaderboardData {
  participants: LeaderboardParticipant[];
  participantCount: number;
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  } | null;
  // Challenge context for progress display
  durationDays: number;
  requiredDays: number;
  challengeName: string;
  challengeType: string; // 'run' | 'marathon' | etc.
  totalDistanceGoal: number;
  // Marathon-specific fields
  targetDurationSeconds: number | null;
  isOffChain: boolean;
}

interface UseLeaderboardResult {
  data: LeaderboardData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}


/**
 * Hook to fetch leaderboard data for a challenge.
 *
 * Uses the existing challenge detail endpoint which returns participants
 * sorted by performance (daysCompleted DESC, totalRunDistance DESC)
 * with computed rank based on position.
 */
export function useLeaderboard(challengeId: string | undefined): UseLeaderboardResult {
  const { user } = useAuth();
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track if we've done the initial fetch to avoid showing loading spinner on refetches
  const hasFetchedRef = useRef(false);

  const fetchLeaderboard = useCallback(async () => {
    if (!challengeId) {
      setError('Challenge ID is required');
      setIsLoading(false);
      return;
    }

    // Only show loading spinner on initial fetch, not refetches
    if (!hasFetchedRef.current) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const challenge: Challenge = await challengesApi.getChallenge(challengeId);

      // Map API participants to leaderboard format
      const participants: LeaderboardParticipant[] = (challenge.participants || []).map((p: Participant) => ({
        rank: p.rank || 0,
        walletAddress: p.walletAddress,
        name: p.user?.username || truncateWallet(p.walletAddress),
        avatar: p.user?.avatarUrl || generateAvatarUrl(p.walletAddress),
        daysCompleted: p.daysCompleted || 0,
        totalRunDistance: p.totalRunDistance || 0,
        totalRunDuration: p.totalRunDuration || 0,
        currentStreak: p.currentStreak || 0,
        isWinner: p.isWinner || false,
        isCurrentUser: user?.walletAddress?.toLowerCase() === p.walletAddress.toLowerCase(),
      }));

      setData({
        participants,
        participantCount: challenge.participantCount || participants.length,
        pagination: challenge.pagination || null,
        durationDays: challenge.durationDays,
        requiredDays: challenge.requiredDays,
        challengeName: challenge.name,
        challengeType: challenge.type,
        totalDistanceGoal: challenge.totalDistanceGoal,
        // Marathon-specific fields
        targetDurationSeconds: challenge.targetDurationSeconds ?? null,
        isOffChain: challenge.isOffChain ?? false,
      });
      hasFetchedRef.current = true;
    } catch (err: any) {
      setError(refineErrorMessage(err));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [challengeId, user?.walletAddress]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchLeaderboard,
  };
}

/**
 * Truncate wallet address for display (e.g., "0x1234...5678")
 */
function truncateWallet(address: string): string {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * Generate a deterministic avatar URL based on wallet address
 */
function generateAvatarUrl(walletAddress: string): string {
  // Use DiceBear API for consistent, unique avatars based on wallet
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${walletAddress}`;
}
